import { Box, Button, CircularProgress, Grid, Typography } from '@mui/material';
import React, { useEffect, useLayoutEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import DigitalSignature from './DigitalSignature';
import { getQuoteDetail, signatureQuote } from '../services/quotes';
import { useAuthToken } from '../hooks/useAuthToken';
import Error from './Error';
import styled from 'styled-components';
import dayjs from 'dayjs';
import Logo from './Logo';

const Container = styled(Box)(({ isMobile }) => ({
  margin: isMobile ? '40px 5vw' : '40px 10vw'
}))

const ContainerErrorMessage = styled(Box)(({ isMobile }) => ({
  width: isMobile ? 'full-width' : 'fit-content',
  color: "#842029",
  backgroundColor: "#f8d7da",
  borderColor: "#f5c2c7",
  padding: '10px 30px',
  marginLeft: 'auto'
}))

const Row = styled(Box)({
  display: "flex",
  justifyContent: 'space-between'
})

const Text = styled(Typography)(({ size = 16, color = '#2B2D2E' }) => ({
  color: color,
  fontSize: `${size}px !important`,
  fontWeight: '450 !important'
}))

const DetailContainer = styled(Box)({
  backgroundColor: '#F5F4F4',
  borderRadius: 12,
  padding: '12px 16px',
  marginTop: 16
})

const Divider = styled(Box)({
  height: 2,
  width: '100%',
  backgroundColor: "#2B2D2E",
  borderRadius: 100,
  marginBottom: 16
})

const QuoteSignature = ({ isMobile, setCurrentBrand }) => {

  const [searchParams] = useSearchParams();

  // Hooks loading
  const [isLoading, setIsLoading] = useState(false);
  const [isFetchLoading, setIsFetchLoading] = useState(false);

  // data
  const [quoteId, setQuoteId] = useState();
  const [userSignature, setUserSignature] = useState();
  const [quoteDetail, setQuoteDetail] = useState();

  //status
  const [isSigned, setIsSigned] = useState(false);
  const [messageError, setMessageError] = useState();
  const [CanSignQuote, setCanSignQuote] = useState(false);

  //camva clean
  const [cleanCanva, setCleanCanva] = useState();


  //hook auth
  const { isLoadingToken, appToken, getAuthToken } = useAuthToken();

  useLayoutEffect(() => {
    setCurrentBrand(searchParams.get("b"));
    setQuoteId(searchParams.get("q"))
    localStorage.setItem("current_brand", searchParams.get("b"));

    getAuthToken();
  }, [])

  useEffect(() => {
    if (quoteId && appToken && !isLoading) {
      setIsSigned(false);
      setMessageError();
      setIsLoading(true);
      getQuoteDetail(quoteId)
        .then(({ data }) => {
          if (data.status == 'success') {
            setQuoteDetail(data.data)
            if (data?.data?.is_signed) {
              setIsSigned(true);
            }
          } else {
            setMessageError("Este cita no existe.")
          }
        })
        .catch((err) => console.log(err))
        .finally(() => setIsLoading(false))
    }

  }, [quoteId, appToken])

  useEffect(() => {
    if (userSignature) {
      setCanSignQuote(true)
      setMessageError()
    }
  }, [userSignature])

  const saveSignature = () => {
    if (userSignature == null || userSignature == '') {
      setMessageError("Debes llenar la firma");
      return;
    }

    const data = {
      quote_id: quoteId,
      user_signature: userSignature
    }

    setMessageError();
    setIsFetchLoading(true);
    signatureQuote(data)
      .then(({ data }) => {
        if (data.status == 'success') {
          setIsSigned(true);
        } else {
          throw data
        }
      })
      .catch((err) => {
        setMessageError(Array.isArray(err?.message) ? err?.message[0] : (err?.message ?? "Error desconocido, intente de nuevo."));
      })
      .finally(() => setIsFetchLoading(false))
  }

  const getDateQuote = (quote) => {

    if(quote?.date && quote?.hour) {
      return dayjs(`${quote?.date} ${quote?.hour}`).format("MMM DD [de] YYYY - HH:mm");
    }

    return '---'

  }

  return (
    <Box padding={"16px"}>
      <Logo/>
      <Container isMobile={isMobile} >
      <Grid container spacing={6}>
        {
          (isLoadingToken || isLoading) ? <Error hasLoader />
            :
            <>
              {
                quoteDetail ?
                  <>
                    {
                      isSigned ?
                        <>
                          <Grid item xs={12} >
                            <Error customTitle={"Esta cita ya se encuentra firmada."} customMessage={" "} linkButton={""} />
                          </Grid>
                        </>
                        :
                        <>
                          <Grid item xs={12} >
                            <Typography variant="title" align="center" color="primary" ><b>Firma afiliado</b></Typography>
                          </Grid>
                          <Grid item xs={12} lg={6} >
                            Firma a continuación para validar el inicio de tu cita médica.
                            <Box padding={'16px'} >
                              <Row>
                                <Text color={"#969595"} >Afiliado</Text>
                                <Text size={18} ><b>{quoteDetail?.client_first_name + " " + quoteDetail?.client_last_name}</b></Text>
                              </Row>
                              <Row>
                                <Text color={"#969595"} >Documento</Text>
                                <Text size={18} ><b>{quoteDetail['number-document'] ?? ''}</b></Text>
                              </Row>
                            </Box>
                            <Divider />
                            <Text size={20} ><b>Datos cita médica</b></Text>
                            <DetailContainer>
                              <Row>
                                <Text color={"#969595"} >Profesional</Text>
                                <Text size={18} ><b>{quoteDetail?.medical_professional_first_name + " " + quoteDetail?.medical_professional_last_name}</b></Text>
                              </Row>
                              <Row>
                                <Text color={"#969595"} >Cuando</Text>
                                <Text size={18} ><b>{getDateQuote(quoteDetail)}</b></Text>
                              </Row>
                              <Row>
                                <Text color={"#969595"} >Tipo de cita</Text>
                                <Text size={18} ><b>{quoteDetail?.type_quote_name}</b></Text>
                              </Row>
                              <Row>
                                <Text color={"#969595"} >Sede</Text>
                                <Text size={18} ><b>{quoteDetail?.venue}</b></Text>
                              </Row>
                              <Row>
                                <Text color={"#969595"} >Modalidad</Text>
                                <Text size={18} ><b>{quoteDetail?.modality_name}</b></Text>
                              </Row>
                            </DetailContainer>
                          </Grid>

                          <Grid item xs={12} lg={6}>
                            <DigitalSignature 
                              setSignature={setUserSignature} 
                              showButton={false} 
                              height={isMobile ? 180: 250} 
                              clean={cleanCanva} 
                            />

                            {messageError &&
                              <ContainerErrorMessage>
                                {messageError}
                              </ContainerErrorMessage>}

                            <Box display={"flex"} justifyContent={"end"} gap={2} marginTop={2} flexDirection={isMobile? 'column': 'row'} >
                              <Button
                                variant="contained"
                                color="secondary"
                                fullWidth={isMobile}
                                onClick={() => {
                                  setCleanCanva(Math.random());
                                  setUserSignature(null);
                                }}
                              >
                                Limpiar
                              </Button>
                              <Button
                                variant="contained"
                                color="primary"
                                fullWidth={isMobile}
                                disabled={!CanSignQuote || isFetchLoading}
                                onClick={saveSignature}
                              >
                                {isFetchLoading ? < CircularProgress size={30} color="primary" /> : 'Guardar'}
                              </Button>
                            </Box>
                          </Grid>
                        </>
                    }
                  </>
                  :

                  <>
                    <Grid item xs={12} >
                      <Error customTitle={messageError} linkButton={""} />
                    </Grid>
                  </>
              }
            </>
        }
      </Grid>
    </Container>
    </Box>
  )
}

export default QuoteSignature;